import {
  useIsMutating,
  useMutation,
  useMutationState,
  useQueryClient,
  type Mutation,
  type MutationStatus,
  type QueryClient,
} from '@github-ui/react-query'
import {announce} from '@github-ui/aria-live'
import {useCallback} from 'react'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {usePageDataUrl} from '@github-ui/pull-request-page-data-tooling/use-page-data-url'
import {
  fetchWithErrorHandling,
  parseJSONWithBetterErrors,
  throwErrorsIfBadResponse,
} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'

import {
  agentMergeMutationKey,
  agentMergeMutationKeyPrefix,
  agentMergeQueryKey,
  agentMergeQueryKeyPrefix,
} from '../../page-data/loaders/use-agent-merge-page-data'
import {
  AgentMergePolicyDisabledError,
  AUTOMATIONS_POLICY_DISABLED_REASON,
  normalizeAgentMergeConfiguration,
  type AgentMergeCheckScope,
  type AgentMergeConfiguration,
  type AgentMergeConfigurationResponse,
  type AgentMergeErrorResponse,
} from '../../page-data/payloads/agent-merge'

type AgentMergeMutationVariables = {
  configuration: AgentMergeConfiguration
  queryKey: ReturnType<typeof agentMergeQueryKey>
  headSha: string
  queuedBehindAnotherWrite: boolean
}

type AgentMergeMutationContext = {
  isNonOwnerDisable: boolean
  previousConfiguration: AgentMergeConfigurationResponse | undefined
}

function agentMergeMutationScopeId(apiURL: string) {
  return `updateAgentMerge:${apiURL}`
}

function updateLiveQueryData(
  queryClient: QueryClient,
  queryKey: ReturnType<typeof agentMergeQueryKey>,
  updater: (current: AgentMergeConfigurationResponse | undefined) => AgentMergeConfigurationResponse | undefined,
) {
  if (!queryClient.getQueryCache().find({queryKey, exact: true})) return

  queryClient.setQueryData<AgentMergeConfigurationResponse>(queryKey, updater)
}

function scopesMatch(left: AgentMergeCheckScope, right: AgentMergeCheckScope) {
  return (
    left.state === right.state &&
    left.actionsOnly === right.actionsOnly &&
    left.configPath === right.configPath &&
    left.checkNames.length === right.checkNames.length &&
    left.checkNames.every((name, index) => name === right.checkNames[index])
  )
}

function mergeCheckNameValidation(
  response: AgentMergeCheckScope | null,
  current: AgentMergeCheckScope | null | undefined,
  previous: AgentMergeCheckScope | null | undefined,
) {
  if (response?.state !== 'configured' || response.unmatchedCheckNames !== null) return response

  const prior = current?.state === 'configured' ? current : previous
  if (!prior || prior.unmatchedCheckNames === null || !scopesMatch(response, prior)) return response

  return {...response, unmatchedCheckNames: prior.unmatchedCheckNames}
}

function mergeConfirmedResponse(
  response: AgentMergeConfigurationResponse,
  current: AgentMergeConfigurationResponse | undefined,
  previous: AgentMergeConfigurationResponse | undefined,
) {
  const automationsPolicyDisabled =
    response.automationsPolicyDisabled ?? current?.automationsPolicyDisabled ?? previous?.automationsPolicyDisabled
  const automationsPolicyDisabledByOrganization =
    response.automationsPolicyDisabledByOrganization ??
    (response.automationsPolicyDisabled === undefined
      ? (current?.automationsPolicyDisabledByOrganization ?? previous?.automationsPolicyDisabledByOrganization)
      : undefined)
  const fixCICheckScope = mergeCheckNameValidation(
    response.fixCICheckScope,
    current?.fixCICheckScope,
    previous?.fixCICheckScope,
  )

  const mergedResponse = {
    ...current,
    ...response,
    fixCICheckScope,
    ...(automationsPolicyDisabled === undefined ? {} : {automationsPolicyDisabled}),
  }
  if (automationsPolicyDisabledByOrganization === undefined) {
    delete mergedResponse.automationsPolicyDisabledByOrganization
  } else {
    mergedResponse.automationsPolicyDisabledByOrganization = automationsPolicyDisabledByOrganization
  }

  return mergedResponse
}

function mergePolicyDisabledResponse(
  configuration: AgentMergeConfigurationResponse | undefined,
  policy: AgentMergePolicyDisabledError['policy'],
) {
  if (!configuration) return configuration

  const {automationsPolicyDisabledByOrganization: previousPolicyScope, ...configurationWithoutPolicyScope} =
    configuration
  const policyScope =
    policy.automationsPolicyDisabledByOrganization ??
    (configuration.automationsPolicyDisabled === true ? previousPolicyScope : undefined)

  return {
    ...configurationWithoutPolicyScope,
    ...policy,
    ...(policyScope === undefined ? {} : {automationsPolicyDisabledByOrganization: policyScope}),
  }
}

// Rolling back a failed write must not resurrect the control when the endpoint is already known to
// be policy-blocked, so the rollback keeps whatever policy state the cache currently holds.
function rollbackWithPolicy(
  current: AgentMergeConfigurationResponse | undefined,
  previous: AgentMergeConfigurationResponse | undefined,
) {
  if (!previous || current?.automationsPolicyDisabled !== true) return previous

  return mergePolicyDisabledResponse(previous, {
    automationsPolicyDisabled: true,
    ...(current.automationsPolicyDisabledByOrganization === undefined
      ? {}
      : {automationsPolicyDisabledByOrganization: current.automationsPolicyDisabledByOrganization}),
  })
}

function agentMergeSavedAnnouncement(response: AgentMergeConfigurationResponse) {
  if (!response.enabled) return 'Agent Merge settings saved. Agent Merge is off.'

  const actionCount = response.allowedActions.length
  return `Agent Merge settings saved. Agent Merge is on with ${actionCount} automatic ${
    actionCount === 1 ? 'action' : 'actions'
  } selected.`
}

type AgentMergeWriteState = {
  headSha: string | undefined
  status: MutationStatus
  submittedAt: number
  errorMessage: string | null
}

function writeSelector(write: Mutation<unknown, Error, unknown, unknown>): AgentMergeWriteState {
  const {status, submittedAt, error} = write.state
  const key = write.options.mutationKey
  return {
    // Carried from the write's own key so a head that no longer matches can be dropped in render.
    headSha: Array.isArray(key) ? (key.at(-1) as string | undefined) : undefined,
    status,
    submittedAt,
    // A policy rejection hides the whole section, so its message has no surface to render on.
    errorMessage: error && !(error instanceof AgentMergePolicyDisabledError) ? error.message : null,
  }
}

function latestWriteErrorMessage(writeStates: AgentMergeWriteState[], headSha: string): string | null {
  writeStates = writeStates.filter(write => write.headSha === headSha)

  // An in-flight write supersedes an earlier failure: reporting it would contradict the optimistic
  // state the user can see. Otherwise only the most recent write decides, so a success clears the
  // error it replaces.
  if (writeStates.some(write => write.status === 'pending')) return null

  return (
    writeStates.reduce<AgentMergeWriteState | null>(
      (latest, write) => (latest && latest.submittedAt >= write.submittedAt ? latest : write),
      null,
    )?.errorMessage ?? null
  )
}

export function useUpdateAgentMergeConfigurationMutation({
  currentHeadSha,
  viewerLogin,
}: {
  currentHeadSha: string
  viewerLogin: string | null
}) {
  const apiURL = usePageDataUrl(PageData.updateAgentMerge)
  const mutationKey = agentMergeMutationKey(apiURL, currentHeadSha)
  const mutationKeyPrefix = agentMergeMutationKeyPrefix(apiURL)
  const queryKeyPrefix = agentMergeQueryKeyPrefix(apiURL)
  const queryClient = useQueryClient()

  const writeStates = useMutationState({filters: {mutationKey: mutationKeyPrefix}, select: writeSelector})
  const errorMessage = latestWriteErrorMessage(writeStates, currentHeadSha)

  // `isMutating` counts writes for every head, so both surfaces (and a head-keyed remount) disable
  // their controls for the whole serialized run rather than only for their own write.
  const isMutating = useIsMutating({mutationKey: mutationKeyPrefix}) > 0

  // Skipping cache work while another write is queued keeps an earlier write from reverting the
  // optimistic state a later one already applied. Read the count imperatively rather than from
  // `useIsMutating` to avoid a stale closure, and compare against 1 because a settling write is
  // still counted as pending inside its own callbacks.
  const isFinalPendingWrite = () => queryClient.isMutating({mutationKey: mutationKeyPrefix}) === 1

  const mutation = useMutation<
    AgentMergeConfigurationResponse | undefined,
    Error,
    AgentMergeMutationVariables,
    AgentMergeMutationContext
  >({
    mutationKey,
    scope: {id: agentMergeMutationScopeId(apiURL)},
    mutationFn: async ({configuration, headSha, queuedBehindAnotherWrite}) => {
      // Serialized writes each send the whole configuration, so a queued write that a later queued
      // write already supersedes has nothing left to save. A write that was never queued always
      // sends: its request is what every later write is waiting behind.
      if (queuedBehindAnotherWrite && !isFinalPendingWrite()) return undefined

      const response = await fetchWithErrorHandling(apiURL, {
        method: 'POST',
        body: configuration,
      })
      const json = (await parseJSONWithBetterErrors(response)) as AgentMergeConfigurationResponse &
        AgentMergeErrorResponse
      if (!response.ok && json.reason === AUTOMATIONS_POLICY_DISABLED_REASON) {
        throw new AgentMergePolicyDisabledError(json.error || 'Agent Merge is disabled by the Automations policy.', {
          automationsPolicyDisabled: true,
          ...(json.automationsPolicyDisabledByOrganization === undefined
            ? {}
            : {automationsPolicyDisabledByOrganization: json.automationsPolicyDisabledByOrganization}),
        })
      }
      throwErrorsIfBadResponse(response, json)
      const normalizedResponse = normalizeAgentMergeConfiguration(json)

      if (normalizedResponse.headSha !== headSha) {
        throw new Error('The pull request changed. Refresh and try again.')
      }

      return normalizedResponse
    },
    onMutate: async ({configuration, queryKey}) => {
      await queryClient.cancelQueries({queryKey})

      const previousConfiguration = queryClient.getQueryData<AgentMergeConfigurationResponse>(queryKey)
      const isNonOwnerDisable =
        previousConfiguration?.enabled === true &&
        !previousConfiguration.viewerOwnsAssignment &&
        configuration.enabled === false
      if (!isNonOwnerDisable) {
        updateLiveQueryData(queryClient, queryKey, current =>
          current ? {...current, ...configuration, ownerLogin: viewerLogin, viewerOwnsAssignment: true} : current,
        )
      }

      return {isNonOwnerDisable, previousConfiguration}
    },
    onError: async (error, {queryKey}, context) => {
      const isPolicyError = error instanceof AgentMergePolicyDisabledError
      if (isPolicyError) {
        await queryClient.cancelQueries({queryKey: queryKeyPrefix})
      }

      // A queued write is about to replace this one, so its failure is already stale — reporting it
      // would contradict the optimistic state the user can see. A policy rejection is the
      // exception: it blocks the whole endpoint, so the queued write cannot succeed either.
      const isFinalWrite = isFinalPendingWrite()
      if (isPolicyError || isFinalWrite) {
        announce(error.message, {assertive: true})
      }

      if (isFinalWrite) {
        updateLiveQueryData(queryClient, queryKey, current =>
          rollbackWithPolicy(current, context?.previousConfiguration),
        )
      }

      if (isPolicyError) {
        queryClient.setQueriesData<AgentMergeConfigurationResponse>({queryKey: queryKeyPrefix}, current =>
          mergePolicyDisabledResponse(current, error.policy),
        )
      }
    },
    onSuccess: (response, {queryKey}, context) => {
      if (!response || !isFinalPendingWrite()) return

      if (context.isNonOwnerDisable) {
        announce(agentMergeSavedAnnouncement(response))
        return
      }

      const current = queryClient.getQueryData<AgentMergeConfigurationResponse>(queryKey)
      const confirmedResponse = mergeConfirmedResponse(response, current, context.previousConfiguration)
      updateLiveQueryData(queryClient, queryKey, () => confirmedResponse)
      announce(agentMergeSavedAnnouncement(confirmedResponse))
    },
    onSettled: () => {
      if (!isFinalPendingWrite()) return

      return queryClient.invalidateQueries({queryKey: queryKeyPrefix, refetchType: 'active'})
    },
  })

  const {mutate, mutateAsync: _mutateAsync, ...mutationState} = mutation

  const startSave = useCallback(
    (configuration: AgentMergeConfiguration) => {
      // Scoped writes run serially, so an in-flight write means this one is queued behind it. The
      // optimistic update still applies right away: `onMutate` runs when `mutate` is called, and
      // only the request itself waits for its turn.
      const startsImmediately = queryClient.isMutating({mutationKey: agentMergeMutationKeyPrefix(apiURL)}) === 0

      mutate({
        configuration,
        queryKey: agentMergeQueryKey(apiURL, currentHeadSha),
        headSha: currentHeadSha,
        queuedBehindAnotherWrite: !startsImmediately,
      })

      return startsImmediately
    },
    [apiURL, currentHeadSha, mutate, queryClient],
  )

  return {
    ...mutationState,
    mutate: startSave,
    isMutating,
    errorMessage,
  }
}
